import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Ghost from './Ghost.jsx';
import { AUTH_BASE } from '../../services/oidcService';
import { useAuthStore } from '../../store/authStore';
import '../web/WebSignInPage.css';

async function openExternal(url) {
  try {
    const { open } = await import('@tauri-apps/plugin-shell');
    await open(url);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * The desktop app before sign-in (platform/desktopGate.js, Plans v2 card 2.1).
 * Covers the whole window, title bar strip included (owner, 26.10.2026); the
 * title bar keeps only a drag strip and the window buttons floating over the
 * top edge (TauriTitleBar `gated`), so the window can still be moved,
 * minimised and closed. The page's 32px padding keeps the card clear of them. "Continue" is the app's existing browser sign-in
 * (authStore.loginWithBrowser: system browser, loopback callback); "Create
 * account" is the IdP's register page. The terms and the tour come after the
 * first sign-in, inside the app, as on the web.
 *
 * `expired` (Plans v2 card 2.4): still signed in, but offline for longer than
 * the signed plan claim's 14 days. The same sign-in, worded for that case;
 * the app also reopens by itself once the server answers (plans/claimExpiry).
 */
export default function DesktopSignInPage({ loading = false, expired = false }) {
  const { t } = useTranslation();
  const loginWithBrowser = useAuthStore((s) => s.loginWithBrowser);
  const cancelLoginWithBrowser = useAuthStore((s) => s.cancelLoginWithBrowser);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);

  const signIn = useCallback(async () => {
    setError(null);
    setWaiting(true);
    const res = await loginWithBrowser();
    if (!res?.success) {
      setWaiting(false);
      if (!res?.cancelled) setError(res?.error || t('desktopSignIn.failed'));
    }
  }, [loginWithBrowser, t]);

  const cancel = useCallback(() => {
    cancelLoginWithBrowser?.();
    setWaiting(false);
  }, [cancelLoginWithBrowser]);

  return (
    <div className="web-signin app-shell-bg flex-1" data-testid={loading ? 'desktop-boot' : 'desktop-sign-in'}>
      <div className="web-signin__card">
        <div className="web-signin__logo">
          <Ghost variant="classic" size={52} animated glow />
        </div>
        {loading ? (
          <p className="web-signin__text" role="status">{t('desktopSignIn.loading')}</p>
        ) : (
          <>
            <h1 className="web-signin__title">{t(expired ? 'desktopSignIn.expiredTitle' : 'desktopSignIn.title')}</h1>
            <p className="web-signin__text">{t(expired ? 'desktopSignIn.expiredText' : 'desktopSignIn.text')}</p>
            {error && <div className="web-signin__error" role="alert">{error}</div>}
            <button type="button" className="web-signin__btn web-signin__btn--primary" onClick={signIn} disabled={waiting}>
              {waiting ? t('desktopSignIn.waiting') : t('desktopSignIn.continue')}
            </button>
            {waiting ? (
              <button type="button" className="web-signin__btn" onClick={cancel}>
                {t('desktopSignIn.cancel')}
              </button>
            ) : (
              <button type="button" className="web-signin__btn" onClick={() => openExternal(`${AUTH_BASE}/register`)}>
                {t('desktopSignIn.register')}
              </button>
            )}
            <p className="web-signin__foot">{t('desktopSignIn.foot')}</p>
          </>
        )}
      </div>
    </div>
  );
}
